import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { Grid } from '@material-ui/core';
import { createStructuredSelector } from 'reselect';
import InvoicesAddressSelectDisplay from 'containers/PrimoAccount/ServiceLocations/InvoicesAddressSelectDisplay';
import { AutoComplete, Span, H1 } from 'components/Ui';
import CaretDown from 'components/Ui/CustomIcons/CaretDown';
import {
  fetchBillingStatements as fetchBillingStatementsRequest,
  getServiceLocationRecentTransactions,
} from 'containers/PrimoAccount/actions';
import { getBillingSummary } from 'containers/PrimoProfile/actions';
import {
  selectBillingStatements,
  selectRecentTransactionAddresses,
  selectBillingAccountId,
  selectServiceLocations,
} from 'containers/PrimoAccount/selectors';
import {
  selectBillingSummary,
  selectCreditCards,
} from 'containers/PrimoProfile/selectors';

import { showDrawer, hideDrawer } from 'containers/DrawerManager/actions';
import moment from 'moment';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import { translateData } from 'utils/translation';
import PastOrdersList from '../../PrimoCommon/PastOrdersList';
import styles from '../styles.scss';

function Invoices({
  fetchBillingStatements,
  billingStatements,
  billingLocations,
  selectedBillingAccountId,
  serviceLocations,
}) {
  const defaultShowOnlyOptions = [
    {
      value: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      label: translateData('Past 30 days'),
    },
    {
      value: moment().subtract(6, 'months').format('YYYY-MM-DD'),
      label: translateData('Past 6 months'),
    },
  ];

  const getBillingAddress = (billingAccountId) =>
    billingLocations.find(
      (billingLocation) => billingLocation.id === billingAccountId,
    );
  const getDefaultServiceLocation = (billingAccountId) =>
    serviceLocations.find(
      (serviceLocation) =>
        serviceLocation.billingAccountId === billingAccountId,
    );

  const [billingLocation, setBillingLocation] = useState(
    getBillingAddress(selectedBillingAccountId),
  );

  const [defaultServiceLocation, setServiceLocation] = useState(
    getDefaultServiceLocation(selectedBillingAccountId),
  );

  const [showState, setShowState] = useState({
    showOnly: defaultShowOnlyOptions[0],
    showOnlyOptions: defaultShowOnlyOptions,
  });

  useEffect(() => {
    if (selectedBillingAccountId) {
      fetchBillingStatements({ billingAccountId: selectedBillingAccountId });
    }
    setBillingLocation(getBillingAddress(selectedBillingAccountId));
    setServiceLocation(getDefaultServiceLocation(selectedBillingAccountId));
    generateShowOnlyOptions();
  }, [selectedBillingAccountId]);

  const handleLoadingBillingStatement = (address) => {
    setBillingLocation(address);
    setServiceLocation(getDefaultServiceLocation(address.id));
    fetchBillingStatements({ billingAccountId: address.id });
    generateShowOnlyOptions();
  };

  const handleSelectedDateFilterChange = (selectedOption) => {
    setShowState({ ...showState, showOnly: selectedOption });
  };

  const generateShowOnlyOptions = () => {
    const currentDate = new Date();
    const toYear = moment(currentDate).year();
    const fromYear = toYear - 1;
    const yearRange = [toYear, fromYear];

    const yearOptions = yearRange.map((year) => ({
      label: String(year),
      value: String(year),
    }));
    setShowState({
      ...showState,
      showOnlyOptions: defaultShowOnlyOptions.concat(yearOptions),
    });
  };

  const formatLabel = (item, asOption = false) => {
    if (asOption) {
      const { label, value } = item;
      const selected = value === showState.showOnly.value;

      return (
        <Fragment>
          <div className={styles.labelContainer}>
            <div className={styles.label}>
              <Span>{label}</Span>
            </div>
            {selected && <div className={styles.selected} />}
          </div>
        </Fragment>
      );
    }
    return item;
  };

  return (
    <article data-tracking={GTM_TRACKING_TAGS.Invoices}>
      <div className={styles.title}>
        <H1>Invoices</H1>
      </div>
      <div className={styles.search}>
        <ValidatorForm onSubmit={() => {}}>
          <Grid className={styles.formContainer} container spacing={4}>
            <Grid item xs={12} sm={12} md={6}>
              <InvoicesAddressSelectDisplay
                className={styles.serviceLocations}
                addresses={billingLocations}
                defaultValue={billingLocation}
                name="billingAddress"
                isBilling
                handleAddressChange={(address) =>
                  handleLoadingBillingStatement(address)
                }
              />
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <AutoComplete
                darkDropdown
                label="Show only:"
                icon={<CaretDown />}
                formatLabel={formatLabel}
                value={showState.showOnly}
                options={showState.showOnlyOptions.map((item) => ({
                  value: item.value,
                  label: formatLabel(item, true),
                }))}
                onChange={handleSelectedDateFilterChange}
                name="showOnly"
                isClearable={false}
                isSearchable={false}
              />
            </Grid>
          </Grid>
        </ValidatorForm>
      </div>
      {defaultServiceLocation && (
        <PastOrdersList
          showOnlyFilter={showState.showOnly.value}
          mystatements={billingStatements}
          currentAddress={billingLocation}
          defaultServiceLocationId={defaultServiceLocation.id}
          serviceLocations={serviceLocations}
        />
      )}
    </article>
  );
}

Invoices.propTypes = {
  fetchBillingStatements: PropTypes.func,
  billingStatements: PropTypes.array,
  billingLocations: PropTypes.array,
  selectedBillingAccountId: PropTypes.string,
  serviceLocations: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  billingStatements: selectBillingStatements(),
  selectedBillingAccountId: selectBillingAccountId(),
  billingSummary: selectBillingSummary(),
  creditCards: selectCreditCards(),
  recentTransactionAddresses: selectRecentTransactionAddresses(),
  serviceLocations: selectServiceLocations(),
});

const mapDispatchToProps = {
  fetchBillingStatements: fetchBillingStatementsRequest,
  getServiceLocationRecentTransactions,
  getBillingSummary,
  showDrawer,
  hideDrawer,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(Invoices);
